import axios from 'axios';

// VI-368 (resume): re-upload a recorded blob that could NOT be finalized over
// the streaming WebSocket (the jobseeker refreshed/disconnected, so the live
// FFmpeg pipe is gone). Hits the server endpoint that runs the same pipeline as
// the WS `process_recording` path. Works for both video and audio via `mode`.

export interface RecordingUploadMeta {
  mode: 'video' | 'audio';
  applicantGuid: string;
  questionId: number;
  questionText: string;
  isLastQuestion: boolean;
  totalQuestions: number;
  s3MetaData: unknown;
}

// VI-900: the server replies 202 as soon as the upload is received, so neither
// the upload nor the wait for that reply should ever go quiet for long. The
// response window is wider: the browser reports 100% once bytes leave it, and on
// a slow uplink they can still be in flight for a while.
const UPLOAD_STALL_MS = 30000;
const RESPONSE_TIMEOUT_MS = 90000;

export interface RecordingUploadResult {
  videoUrl?: string;
  audioUrl?: string;
  thumbnailUrl?: string;
  transcription?: string;
}

export const uploadRecordingHttp = async (
  blob: Blob,
  meta: RecordingUploadMeta,
  onProgress?: (fraction: number) => void,
): Promise<RecordingUploadResult> => {
  // MediaRecorder blobs carry a codec parameter (e.g. "video/webm;codecs=vp9,opus")
  // and can occasionally have an empty/odd type once round-tripped through
  // IndexedDB. The multipart part's Content-Type comes from the blob's type, so
  // normalize it to a clean base mime type the server's file filter accepts —
  // otherwise the upload is rejected as "Invalid file type".
  const baseType = (blob.type || '').split(';')[0].trim().toLowerCase();
  const cleanType =
    baseType && (baseType.startsWith('video/') || baseType.startsWith('audio/'))
      ? baseType
      : meta.mode === 'audio'
        ? 'audio/webm'
        : 'video/webm';
  const ext = cleanType.includes('mp4') ? 'mp4' : cleanType.includes('ogg') ? 'ogg' : 'webm';
  const fileToSend = blob.type === cleanType ? blob : new Blob([blob], { type: cleanType });

  const form = new FormData();
  form.append('media', fileToSend, `recording.${ext}`);
  form.append('mode', meta.mode);
  form.append('applicantGuid', meta.applicantGuid);
  form.append('questionId', String(meta.questionId));
  form.append('questionText', meta.questionText);
  form.append('isLastQuestion', String(meta.isLastQuestion));
  form.append('totalQuestions', String(meta.totalQuestions));
  form.append('s3MetaData', JSON.stringify(meta.s3MetaData ?? {}));

  // VI-900: abort if the upload stops making progress, or if the server doesn't
  // reply soon after the upload finished — the candidate gets an error and can
  // retry instead of waiting on a spinner indefinitely.
  const controller = new AbortController();
  let watchdog: ReturnType<typeof setTimeout> | undefined;
  let abortReason = '';
  const armWatchdog = (ms: number, reason: string) => {
    if (watchdog) clearTimeout(watchdog);
    watchdog = setTimeout(() => {
      abortReason = reason;
      controller.abort();
    }, ms);
  };
  armWatchdog(UPLOAD_STALL_MS, 'upload stalled');

  try {
    const response = await axios.post('/api/v1/media/submit-recording', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
      signal: controller.signal,
      // A retry would re-upload the whole recording behind the candidate's back;
      // instead the failure is shown and the candidate retries Submit.
      'axios-retry': { retries: 0 },
      onUploadProgress: evt => {
        const fraction = evt.total ? evt.loaded / evt.total : undefined;
        if (fraction !== undefined && fraction >= 1) {
          armWatchdog(RESPONSE_TIMEOUT_MS, 'no response from server');
        } else {
          armWatchdog(UPLOAD_STALL_MS, 'upload stalled');
        }
        if (onProgress && fraction !== undefined) onProgress(fraction);
      },
    });
    return response.data?.data ?? {};
  } catch (err) {
    if (abortReason) throw new Error(`Recording upload aborted: ${abortReason}`);
    throw err;
  } finally {
    if (watchdog) clearTimeout(watchdog);
  }
};
